import { useState } from 'react';
import { CheckCircle, Loader2, MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ZoningCheckForm = ({ suburb = '', sourceArticle = '' }) => {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', address: '', suburb: suburb, question: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.address) {
      setError('Please fill in your first/last name, email, and property address.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/forms/zoning-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          name: `${form.first_name} ${form.last_name}`.trim(),
          source_article: sourceArticle,
        }),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="my-12 bg-[#042E0C] rounded-2xl p-8 text-center" data-testid="zoning-form-success">
        <CheckCircle size={48} className="mx-auto text-[#C4A36B] mb-4" />
        <h3 className="text-2xl font-light text-white mb-2">Thank You</h3>
        <p className="text-white/70">Our town planning team will review your property and contact you within 48 hours with a personalised zoning assessment.</p>
      </div>
    );
  }

  return (
    <div className="my-12 bg-gradient-to-br from-[#042E0C] to-[#0a4a15] rounded-2xl p-8 lg:p-10" data-testid="zoning-check-form">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-full bg-[#C4A36B]/20 flex items-center justify-center">
          <MapPin size={20} className="text-[#C4A36B]" />
        </div>
        <h3 className="text-2xl font-light text-white">Free Zoning Check</h3>
      </div>
      <p className="text-white/60 mb-6 text-sm">Not sure what you can build on your property? Our team will review your zoning, overlays, and constraints — completely free, no obligation.</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <input
              data-testid="zoning-first-name"
              value={form.first_name}
              onChange={(e) => setForm(f => ({ ...f, first_name: e.target.value }))}
              placeholder="First name *"
              className="w-full px-4 py-3 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm"
            />
          </div>
          <div>
            <input
              data-testid="zoning-last-name"
              value={form.last_name}
              onChange={(e) => setForm(f => ({ ...f, last_name: e.target.value }))}
              placeholder="Last name *"
              className="w-full px-4 py-3 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <input
              data-testid="zoning-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="Email address *"
              className="w-full px-4 py-3 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm"
            />
          </div>
          <div>
            <input
              data-testid="zoning-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
              placeholder="Phone number"
              className="w-full px-4 py-3 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <input
              data-testid="zoning-address"
              value={form.address}
              onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))}
              placeholder="Property address *"
              className="w-full px-4 py-3 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm"
            />
          </div>
          <div>
            <input
              data-testid="zoning-suburb"
              value={form.suburb}
              onChange={(e) => setForm(f => ({ ...f, suburb: e.target.value }))}
              placeholder="Suburb"
              className="w-full px-4 py-3 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm"
            />
          </div>
        </div>

        <div>
          <textarea
            data-testid="zoning-question"
            value={form.question}
            onChange={(e) => setForm(f => ({ ...f, question: e.target.value }))}
            placeholder="What would you like to know about your property's zoning? (optional)"
            rows={3}
            className="w-full px-4 py-3 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm resize-none"
          />
        </div>

        {error && <p className="text-red-400 text-sm">{error}</p>}

        <button
          type="submit"
          data-testid="zoning-submit"
          disabled={submitting}
          className="w-full md:w-auto px-8 py-3 bg-[#C4A36B] text-[#042E0C] rounded-lg font-medium hover:bg-[#C4A36B]/90 transition-colors disabled:opacity-50 text-sm"
        >
          {submitting ? <Loader2 size={16} className="animate-spin mx-auto" /> : 'Request Free Zoning Check'}
        </button>

        <p className="text-white/30 text-xs">No cost. No obligation. We simply give you the facts about what you can build.</p>
      </form>
    </div>
  );
};

export default ZoningCheckForm;
